import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Icon from "components/ShellV2/Icon";

/* Generic data table (port of the design prototype's DataTable in
   archetypes.jsx). columns: [{key, label, align, width, render(value, row),
   sort: false}]. Search, column sort and pagination are client-side. */
export default function DataTable({
  columns,
  rows,
  footer,
  onRow,
  maxRows,
  search,
  searchKeys,
  emptyText,
  filters,
}) {
  const { t } = useTranslation(["common"]);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const per = maxRows || 9;

  const norm = (v) => {
    if (v == null) return "";
    if (typeof v === "object") return (Array.isArray(v) ? v[0] : JSON.stringify(v)) ?? "";
    return v.toString().toLowerCase();
  };

  let processed = rows;
  if (q.trim()) {
    const ql = q.trim().toLowerCase();
    const keys = searchKeys || columns.map((c) => c.key);
    processed = processed.filter((row) => keys.some((k) => norm(row[k]).includes(ql)));
  }
  if (sort.key) {
    processed = [...processed].sort((a, b) => {
      let av = a[sort.key];
      let bv = b[sort.key];
      if (typeof av === "object") av = Array.isArray(av) ? av[0] : JSON.stringify(av);
      if (typeof bv === "object") bv = Array.isArray(bv) ? bv[0] : JSON.stringify(bv);
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * sort.dir;
      return String(av ?? "").localeCompare(String(bv ?? ""), "pt", { numeric: true }) * sort.dir;
    });
  }

  useEffect(() => {
    setPage(1);
  }, [q, sort.key, sort.dir]);

  const pages = Math.max(1, Math.ceil(processed.length / per));
  const safePage = Math.min(page, pages);
  const view = processed.slice((safePage - 1) * per, safePage * per);
  const toggleSort = (c) => {
    if (c.sort === false) return;
    setSort((s) => (s.key === c.key ? { key: c.key, dir: -s.dir } : { key: c.key, dir: 1 }));
  };

  const defaultEmpty = q
    ? t("table.no_results_for", { defaultValue: "Sem resultados para “{{q}}”.", q })
    : t("table.no_data", { defaultValue: "Sem dados disponíveis." });

  return (
    <div>
      {(search || filters) && (
        <div className="rec-toolbar">
          {search && (
            <div className="rec-input-wrap max-w-[300px] flex-1">
              <Icon name="search" />
              <input
                className="rec-input"
                placeholder={
                  typeof search === "string"
                    ? search
                    : t("table.search", { defaultValue: "Procurar…" })
                }
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          )}
          {q && (
            <button type="button" className="rec-btn-ghost rec-btn-sm" onClick={() => setQ("")}>
              <Icon name="close" />
              {t("table.clear", { defaultValue: "Limpar" })}
            </button>
          )}
          <div className="grow" />
          {filters}
        </div>
      )}
      <div className="rec-tbl-wrap">
        <table className="rec-tbl rec-tbl-cards">
          <thead>
            <tr>
              {columns.map((c) => {
                const sortable = c.sort !== false && c.label;
                const isSorted = sort.key === c.key;
                return (
                  <th
                    key={c.key}
                    style={{
                      textAlign: c.align || "left",
                      width: c.width,
                      cursor: sortable ? "pointer" : "default",
                      userSelect: "none",
                    }}
                    onClick={sortable ? () => toggleSort(c) : undefined}
                  >
                    <span
                      className="inline-flex items-center gap-1"
                      style={{ flexDirection: c.align === "right" ? "row-reverse" : "row" }}
                    >
                      {c.label}
                      {sortable && (
                        <Icon
                          name={
                            isSorted
                              ? (sort.dir > 0 && "arrow_upward") || "arrow_downward"
                              : "unfold_more"
                          }
                          style={{ fontSize: 14, opacity: isSorted ? 0.9 : 0.3 }}
                        />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {view.map((row, i) => (
              <tr
                key={row.id ?? i}
                onClick={onRow ? () => onRow(row) : undefined}
                style={{ cursor: onRow ? "pointer" : "default" }}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    data-label={typeof c.label === "string" && c.label ? c.label : undefined}
                    style={{ textAlign: c.align || "left" }}
                  >
                    {c.render ? c.render(row[c.key], row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
            {view.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: "center", padding: 40, color: "var(--ink-3)" }}
                >
                  {emptyText || defaultEmpty}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {footer && processed.length > 0 && (
        <div className="rec-tbl-foot">
          <span className="text-[13px] text-ink-3">
            {t("table.showing", {
              defaultValue: "A mostrar {{shown}} de {{total}} registos",
              shown: view.length,
              total: processed.length,
            })}
          </span>
          <div className="rec-pager">
            <button
              type="button"
              disabled={safePage === 1}
              onClick={() => setPage(1)}
              aria-label={t("table.first_page", { defaultValue: "Primeira página" })}
            >
              <Icon name="first_page" style={{ fontSize: 18 }} />
            </button>
            <button
              type="button"
              disabled={safePage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label={t("table.prev_page", { defaultValue: "Anterior" })}
            >
              <Icon name="chevron_left" style={{ fontSize: 18 }} />
            </button>
            {Array.from({ length: Math.min(pages, 5) }).map((_, i) => {
              const base = Math.min(Math.max(1, safePage - 2), Math.max(1, pages - 4));
              const n = base + i;
              return n <= pages ? (
                <button
                  key={n}
                  type="button"
                  className={safePage === n ? "on" : ""}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              ) : null;
            })}
            <button
              type="button"
              disabled={safePage === pages}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              aria-label={t("table.next_page", { defaultValue: "Seguinte" })}
            >
              <Icon name="chevron_right" style={{ fontSize: 18 }} />
            </button>
            <button
              type="button"
              disabled={safePage === pages}
              onClick={() => setPage(pages)}
              aria-label={t("table.last_page", { defaultValue: "Última página" })}
            >
              <Icon name="last_page" style={{ fontSize: 18 }} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node,
      align: PropTypes.oneOf(["left", "center", "right"]),
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      render: PropTypes.func,
      sort: PropTypes.bool,
    })
  ).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  footer: PropTypes.bool,
  onRow: PropTypes.func,
  maxRows: PropTypes.number,
  search: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  searchKeys: PropTypes.arrayOf(PropTypes.string),
  emptyText: PropTypes.node,
  filters: PropTypes.node,
};

DataTable.defaultProps = {
  footer: true,
  onRow: undefined,
  maxRows: undefined,
  search: true,
  searchKeys: undefined,
  emptyText: undefined,
  filters: undefined,
};
